import RouteNotFound from "@/components/RouteNotFound";
import ResetPasswordAuthorized from "@/containers/reset-password/ResetPasswordAuthorized";
import ResetPasswordNotAuthorized from "@/containers/reset-password/ResetPasswordNotAuthorized";
import SignIn from "@/containers/sign-in";
import UserProfile from "@/containers/user-profile";
import { AccountProvider } from "@/modules/account/components/AccountContext";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useRef } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";

import SurveyCreator from "@/containers/survey/SurveyCreator";
import SurveyEditor from "@/containers/survey/SurveyEditor";
import SurveyList from "@/containers/survey/SurveyList";
import SurveyTemplateCreator from "@/containers/survey/SurveyTemplateCreator";
import SurveyTemplateEditor from "@/containers/survey/SurveyTemplateEditor";
import SurveyTemplateList from "@/containers/survey/SurveyTemplateList";
import ClientCreator from "@/modules/client/components/ClientCreator";
import ClientEditor from "@/modules/client/components/ClientEditor";
import ClientList from "@/modules/client/components/ClientList";
import ShiftReportEditor from "@/modules/shiftReports/components/ShiftReportEditor";
import StoreCreator from "@/modules/store/components/StoreCreator";
import StoreEditor from "@/modules/store/components/StoreEditor";
import StoreList from "@/modules/store/components/StoreList";
import UserCreator from "@/modules/users/components/UserCreator";
import UsersDirectory from "@/modules/users/components/UsersDirectory";
import UsersList from "@/modules/users/components/UsersList";

import { LayoutSwitcher } from "@/components/layout";
import Homepage from "@/components/Homepage";

import RouteGuard from "@/components/RouteGuard";
import StructureAssignmentsRouteGuard from "@/components/StructureAssignmentsRouteGuard";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/components/ui/use-toast";
import {
  PATH_AGREEMENT,
  PATH_AGREEMENTS,
  PATH_CONTRACTORS_STUDENTS,
  PATH_ALL,
  PATH_CLIENTS,
  PATH_CONTRACTOR,
  PATH_CONTRACTOR_VISITS,
  PATH_CONTRACTOR_VISITS_EDIT,
  PATH_CONTRACTORS,
  PATH_CONTRACTOR_CARDS,
  PATH_CONTRACTOR_CARD,
  PATH_CONTRACTOR_PANEL_PLANNED_HOURS,
  PATH_DEFAULT_STORE_PROJECT_ASSIGNEES,
  PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_EDIT,
  PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_NEW,
  PATH_DICTS,
  PATH_PASSWORD_CHANGE,
  PATH_PASSWORD_RESET,
  PATH_PAYROLL_AGGREGATION,
  PATH_PAYROLL_AGGREGATION_DETAIL,
  PATH_PAYROLL_INSPECTION,
  PATH_PAYROLL_INSPECTION_DETAIL,
  PATH_PAYROLL_PREVIEW,
  PATH_PAYROLL_SPLIT,
  PATH_PAYROLL_SPLIT_DETAIL,
  PATH_PAYROLLS,
  PATH_PERMISSION_DENIED,
  PATH_PROJECT_CONTRACTOR_VISIT,
  PATH_PROJECT_CONTRACTOR_VISIT_NEW,
  PATH_PROJECT_CONTRACTOR_VISITS,
  PATH_PROJECT_CONTRACTOR_VISITS_EDITOR_DIRECTORY,
  PATH_PROJECT_DEPARTMENT_BASED_ASSIGNMENTS,
  PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT,
  PATH_PROJECT_LIMITS,
  PATH_PROJECT_PERIOD_CONFIRMATION,
  PATH_PROJECT_RATE_EDIT,
  PATH_PROJECT_RATE_NEW,
  PATH_PROJECT_RATES,
  PATH_PROJECT_ROLE_BASED_ASSIGNMENTS,
  PATH_PROJECT_ROLE_EDIT,
  PATH_PROJECT_ROLE_NEW,
  PATH_PROJECT_ROLES,
  PATH_PROJECT_SCHEDULES,
  PATH_PROJECT_SETTLEMENTS,
  PATH_PROJECT_SETTLEMENTS_DETAILS,
  PATH_PROJECT_STORES,
  PATH_PROJECT_WITH_ID,
  PATH_PROJECTS,
  PATH_PROJECTS_ROLE_BASED_ASSIGNMENTS,
  PATH_PROJECTS_CONTRACTORS_PLANNED_HOURS_WIZARD,
  PATH_INVOICE_SPECIFICATIONS,
  PATH_INVOICE_SPECIFICATIONS_DETAILS,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_NEW,
  PATH_PUBLIC_QUESTIONNAIRE,
  PATH_ROOT,
  PATH_SHIFT_REPORTS,
  PATH_SIGN_IN,
  PATH_STORES,
  PATH_STRUCTURE_ASSIGNMENT,
  PATH_STRUCTURE_ASSIGNMENT_NEW,
  PATH_STRUCTURE_ASSIGNMENTS,
  PATH_SURVEYS,
  PATH_TEMPLATES,
  PATH_TIMESHEET_ENTRIES,
  PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS,
  PATH_TIMESHEET_ENTRIES_CONTRACTORS,
  PATH_TIMESHEET_ENTRIES_SHIFT_REPORTS,
  PATH_UPDATES,
  PATH_USER,
  PATH_USER_PROFILE,
  PATH_USERS,
  PATH_PROJECT_INVOICE_SPECIFICATIONS_NEW,
  PATH_PROJECTS_CONTRACTOR_CARDS,
  PATH_PROJECTS_CONTRACTOR_CARD,
  PATH_PAYROLL,
  PATH_STORE_PROJECT_ASSIGNEES,
  PATH_STORE_PROJECT_ASSIGNEES_EDIT,
  PATH_STORE_PROJECT_ASSIGNEES_NEW,
  PATH_PROJECT_MANAGERS,
  PATH_CLIENT,
  PATH_CONTEST_AGREEMENTS,
  PATH_CONTEST_AGREEMENT,
  PATH_CONTEST_AGREEMENTS_LINK,
  PATH_AUDIT_LOGS,
  PATH_SCHEDULES_REPORT,
  PATH_LIMITS_REPORT,
  PATH_CONTRACTORS_PLANNED_HOURS_APPROVALS,
  PATH_EMAIL_RECIPIENTS,
  PATH_EMAIL_TYPES,
  PATH_EMAIL_TYPE,
  PATH_SENTRY_PLAYGROUND,
} from "@/constants";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useTranslation } from "react-i18next";

import AdminGuard from "@/components/AdminGuard";
import AuthGuard from "@/components/AuthGuard";
import appConfiguration from "@/components/cfg/AppCfg";
import DictionaryRoutes from "@/components/dictionaries/DictionaryRoutes";
import RouteDenied from "@/components/RouteDenied";
import RouteChangeListener from "@/containers/application/RouteChangeListener";
import AgreementCreator from "@/modules/agreement/components/AgreementCreator";
import AgreementDirectory from "@/modules/agreement/components/AgreementDirectory";
import AgreementsList from "@/modules/agreement/components/AgreementsList";
import QuestionnaireDirectory from "@/modules/agreement/questionnaires/components/QuestionnaireDirectory";
import ContractorCreator from "@/modules/contractor/components/ContractorCreator";
import ContractorDirectory from "@/modules/contractor/components/ContractorDirectory";
import ContractorsList from "@/modules/contractor/components/ContractorsList";
import ContractorStudentsList from "@/modules/contractor/students/components/ContractorStudentsList";
import { ContractorPanelVisitsDirectory } from "@/modules/contractor-panel/visits/components/ContractorPanelVisitsDirectory";
import { ContractorPanelVisitsEditor } from "@/modules/contractor-panel/visits/components/ContractorPanelVisitsEditor";
import { PayrollAggregationDirectory } from "@/modules/payrolls/components/PayrollAggregationDirectory";
import { PayrollInspectionDirectory } from "@/modules/payrolls/components/PayrollInspectionDirectory";
import { PayrollPreviewDirectory } from "@/modules/payrolls/components/PayrollPreviewDirectory";
import { PayrollsDirectory } from "@/modules/payrolls/components/PayrollsDirectory";
import { PayrollSplitDirectory } from "@/modules/payrolls/components/PayrollSplitDirectory";
import { PayrollInspectionDetail } from "@/modules/payrolls/components/PayrollInspectionDetail";
import { PayrollAggregationDetail } from "@/modules/payrolls/components/PayrollAggregationDetail";
import { PayrollSplitDetail } from "@/modules/payrolls/components/PayrollSplitDetail";
import { ContractorsAssignmentsDepartmentBasedDirectory } from "@/modules/projects/components/contractors-assignments/components/ContractorsAssignmentsDepartmentBasedDirectory";
import { ContractorsVisitsDirectory } from "@/modules/projects/components/contractors-visits/components/ContractorsVisitsDirectory";
import { ContractorVisitDirectory } from "@/modules/projects/components/contractors-visits/components/ContractorVisitDirectory";
import { ContractorVisitsCreator } from "@/modules/projects/components/contractors-visits/components/ContractorVisitsCreator";
import { ContractorVisitsEditorDirectory } from "@/modules/projects/components/contractors-visits/components/ContractorVisitsEditorDirectory";
import ProjectInvoiceSpecificationsEditor from "@/modules/projects/components/invoiceSpecifications/ProjectInvoiceSpecificationsEditor";
import ProjectLimit from "@/modules/projects/components/limits/ProjectLimit";
import ProjectPeriodConfirmation from "@/modules/projects/components/period-confirmation/ProjectPeriodConfirmation";
import ProjectCreator from "@/modules/projects/components/ProjectCreator";
import ProjectEditor from "@/modules/projects/components/ProjectEditor";
import ProjectList from "@/modules/projects/components/ProjectsList";
import ProjectStoreList from "@/modules/projects/components/ProjectStoreList";
import ProjectRateCreator from "@/modules/projects/components/rates/ProjectRateCreator";
import ProjectRateEditor from "@/modules/projects/components/rates/ProjectRateEditor";
import ProjectRateList from "@/modules/projects/components/rates/ProjectRateList";
import { ProjectRoleCreator } from "@/modules/projects/components/roles/ProjectRoleCreator";
import { ProjectRoleEditor } from "@/modules/projects/components/roles/ProjectRoleEditor";
import ProjectRoleList from "@/modules/projects/components/roles/ProjectRoleList";
import ProjectSchedules from "@/modules/projects/components/schedules/ProjectSchedules";
import ProjectSettlementsList from "@/modules/projects/components/settlements/ProjectSettlementsList";
import ProjectSettlementsDetails from "@/modules/projects/components/settlementsDetails/ProjectSettlementsDetails";
import ProjectSettlementsDirectory from "@/modules/projectSettlements/components/ProjectSettlementsDirectory";
import ShiftReportDirectory from "@/modules/shiftReports/components/ShiftReportDirectory";
import { StructureAssignmentsCreator } from "@/modules/structure-assignments/components/StructureAssignmentsCreator";
import { StructureAssignmentsDirectory } from "@/modules/structure-assignments/components/StructureAssignmentsDirectory";
import { StructureAssignmentsEditor } from "@/modules/structure-assignments/components/StructureAssignmentsEditor";
import { TimesheetEntriesContractorsEditor } from "@/modules/timesheet-entries/components/TimesheetEntriesContractorsEditor";
import { TimesheetEntriesContractorsVisitsDirectory } from "@/modules/timesheet-entries/components/TimesheetEntriesContractorsVisitsDirectory";
import { TimesheetEntriesDirectory } from "@/modules/timesheet-entries/components/TimesheetEntriesDirectory";
import { TimesheetEntriesShiftReportsDirectory } from "@/modules/timesheet-entries/components/TimesheetEntriesShiftReportsDirectory";
import Updates from "@/modules/updates/components/Updates";
import { NetworkDetector } from "@/services/networkDetector";
import { Operation } from "@/types/permissions";
import { ContractorsAssignmentsRoleBasedDirectory } from "@/modules/projects/components/contractors-assignments/components/ContractorsAssignmentsRoleBasedDirectory";
import ProjectInvoiceSpecificationsCreator from "@/modules/projects/components/invoiceSpecifications/ProjectInvoiceSpecificationsCreator";
import { ContractorCardsList } from "@/modules/projects/components/contractor-cards/components/ContractorCardsList";
import { ContractorCardDirectory } from "@/modules/projects/components/contractor-cards/components/ContractorCardDirectory";
import { ContractorPanelCardDirectory } from "@/modules/contractor-panel/cards/components/ContractorPanelCardDirectory";
import { ContractorPanelCardsDirectory } from "@/modules/contractor-panel/cards/components/ContractorPanelCardsDirectory";
import { ContractorPanelPlannedHoursDirectory } from "@/modules/contractor-panel/planned-hours/components/ContractorPanelPlannedHoursDirectory";
import ContractorPlannedHoursRouteGuard from "@/modules/contractor-panel/planned-hours/components/ContractorPlannedHoursRouteGuard";
import { DefaultStoreProjectAssigneesDirectory } from "@/modules/defaultStoreProjectAssignees/components/DefaultStoreProjectAssigneesDirectory";
import DefaultStoreProjectAssigneesCreator from "@/modules/defaultStoreProjectAssignees/components/DefaultStoreProjectAssigneesCreator";
import DefaultStoreProjectAssigneesEditor from "@/modules/defaultStoreProjectAssignees/components/DefaultStoreProjectAssigneesEditor";
import { PayrollStatusBasedRedirect } from "@/modules/payrolls/components/PayrollStatusBasedRedirect";
import { StoreProjectAssigneesDirectory } from "@/modules/storeProjectAssignees/StoreProjectAssigneesDirectory";
import StoreProjectAssigneesCreator from "@/modules/storeProjectAssignees/components/StoreProjectAssigneesCreator";
import StoreProjectAssigneesEditor from "@/modules/storeProjectAssignees/components/StoreProjectAssigneesEditor";
import { ProjectManagersDirectory } from "@/modules/projects/components/managers/ProjectManagersDirectory";
import ContestAgreementsList from "@/modules/contest-agreements/components/ContestAgreementsList";
import { ContestAgreementDirectory } from "@/modules/contest-agreements/components/ContestAgreementDirectory";
import { ContestAgreements } from "@/modules/contest-agreements/components/ContestAgreements";
import { AuditLogsDirectory } from "@/modules/auditLogs/components/AuditLogsDirectory";
import { AuditLogDetails } from "@/modules/auditLogs/components/AuditLogDetails";
import { SchedulesReport } from "@/modules/schedulesReport/components/SchedulesReport";
import { LimitsReport } from "@/modules/limitsReport/components/LimitsReport";
import ContractorsPlannedHoursApprovalsList from "@/modules/contractors-planned-hours-approvals/components/ContractorsPlannedHoursApprovalsList";
import { InvoiceSpecificationsList } from "@/modules/invoiceSpecifications/components/InvoiceSpecificationsList";
import { InvoiceSpecificationDetails } from "@/modules/invoiceSpecifications/components/InvoiceSpecificationDetails";
import SentryPlayground from "@/containers/sentry-playground/SentryPlayground";
import { ContractorsPlannedHoursWizardDirectory } from "@/modules/contractorsPlannedHoursWizard/components/ContractorsPlannedHoursWizardDirectory";
import { EmailRecipientsDirectory } from "@/modules/email-recipients";
import {
  EmailTypeEditorDirectory,
  EmailTypesList,
} from "@/modules/email-types";

export const createQueryClient = (
  t: (key: string) => string,
  { toast }: ReturnType<typeof useToast>,
) => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Make it work globally only for errors that are not related to network issues
        // Network issues error are handled in useCachedQuery and useCachedInfiniteQuery
        NetworkDetector.getStatus().isOnline &&
          toast({
            title: t("errors.genericAPIErrorTitle"),
            description: error.message,
          });
        query.meta?.onError?.();
      },
      onSuccess: (data, query) => {
        query.meta?.onSuccess?.(data);
      },
    }),
  });
};

export default function Application() {
  const { t } = useTranslation();
  const toasts = useToast();
  const queryClient = useRef(createQueryClient(t, toasts));

  return (
    <Router>
      <AccountProvider queryClient={queryClient.current}>
        <APIProvider apiKey={appConfiguration.googleMapsApiKey}>
          <QueryClientProvider client={queryClient.current}>
            <Toaster />
            <RouteChangeListener />
            <Routes>
              <Route
                path={PATH_ROOT}
                element={
                  <AuthGuard>
                    <LayoutSwitcher>
                      <Homepage />
                    </LayoutSwitcher>
                  </AuthGuard>
                }
              />
              <Route
                path={PATH_SURVEYS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SurveyList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_SURVEYS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SurveyCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_SURVEYS}/:id`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SurveyEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route path={PATH_SIGN_IN} element={<SignIn />} />
              <Route
                path={PATH_PUBLIC_QUESTIONNAIRE}
                element={<QuestionnaireDirectory />}
              />
              <Route
                path={PATH_PASSWORD_RESET}
                element={<ResetPasswordNotAuthorized />}
              />
              <Route
                path={PATH_PASSWORD_CHANGE}
                element={
                  <AuthGuard>
                    <LayoutSwitcher>
                      <ResetPasswordAuthorized />
                    </LayoutSwitcher>
                  </AuthGuard>
                }
              />
              <Route
                path={PATH_USER_PROFILE}
                element={
                  <AuthGuard>
                    <LayoutSwitcher>
                      <UserProfile />
                    </LayoutSwitcher>
                  </AuthGuard>
                }
              />
              <Route
                path={PATH_SENTRY_PLAYGROUND}
                element={
                  <AuthGuard>
                    <AdminGuard>
                      <LayoutSwitcher>
                        <SentryPlayground />
                      </LayoutSwitcher>
                    </AdminGuard>
                  </AuthGuard>
                }
              />
              <Route
                path={PATH_TEMPLATES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SurveyTemplateList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_TEMPLATES}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SurveyTemplateCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_TEMPLATES}/:id`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SurveyTemplateEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CLIENT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ClientEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_CLIENTS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ClientCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CLIENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ClientList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_AGREEMENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <AgreementsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_AGREEMENTS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <AgreementCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_AGREEMENT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <AgreementDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTORS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_CONTRACTORS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTORS_STUDENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorStudentsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTOR}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_USERS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <UsersList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_USERS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <UserCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_USER}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <UsersDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_STORES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StoreList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_STORES}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StoreCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_STORES}/:id`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StoreEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />

              <Route
                path={`${PATH_DICTS}/:dict/:id?`}
                element={<DictionaryRoutes />}
              />
              <Route
                path={PATH_INVOICE_SPECIFICATIONS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <InvoiceSpecificationsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_INVOICE_SPECIFICATIONS_DETAILS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <InvoiceSpecificationDetails />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS_SETTLEMENTS_DIRECTORY}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectSettlementsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectSettlementsDetails />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={
                  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT
                }
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectInvoiceSpecificationsEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={
                  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_NEW
                }
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectInvoiceSpecificationsCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />

              <Route
                path={PATH_PROJECT_WITH_ID}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_STORES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectStoreList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_ROLES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectRoleList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_SCHEDULES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectSchedules />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_ROLE_BASED_ASSIGNMENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsAssignmentsRoleBasedDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS_ROLE_BASED_ASSIGNMENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsAssignmentsRoleBasedDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS_CONTRACTORS_PLANNED_HOURS_WIZARD}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsPlannedHoursWizardDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_DEPARTMENT_BASED_ASSIGNMENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsAssignmentsDepartmentBasedDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_CONTRACTOR_VISITS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsVisitsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_CONTRACTOR_VISITS_EDITOR_DIRECTORY}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorVisitsEditorDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_MANAGERS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectManagersDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_CONTRACTOR_VISIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorVisitDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_CONTRACTOR_VISIT_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorVisitsCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_STRUCTURE_ASSIGNMENTS}
                element={
                  <StructureAssignmentsRouteGuard>
                    <LayoutSwitcher>
                      <StructureAssignmentsDirectory />
                    </LayoutSwitcher>
                  </StructureAssignmentsRouteGuard>
                }
              />
              <Route
                path={PATH_STRUCTURE_ASSIGNMENT}
                element={
                  <StructureAssignmentsRouteGuard>
                    <LayoutSwitcher>
                      <StructureAssignmentsEditor />
                    </LayoutSwitcher>
                  </StructureAssignmentsRouteGuard>
                }
              />
              <Route
                path={PATH_STRUCTURE_ASSIGNMENT_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StructureAssignmentsCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_RATES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectRateList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_RATE_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectRateCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_RATE_EDIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectRateEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_SETTLEMENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectSettlementsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_SETTLEMENTS_DETAILS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectSettlementsDetails />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectInvoiceSpecificationsEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_INVOICE_SPECIFICATIONS_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectInvoiceSpecificationsCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_PROJECTS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_ROLE_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectRoleCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_ROLE_EDIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectRoleEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_LIMITS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectLimit />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECT_PERIOD_CONFIRMATION}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ProjectPeriodConfirmation />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <TimesheetEntriesContractorsVisitsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_TIMESHEET_ENTRIES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <TimesheetEntriesDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_TIMESHEET_ENTRIES_CONTRACTORS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <TimesheetEntriesContractorsEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_SHIFT_REPORTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ShiftReportDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_TIMESHEET_ENTRIES_SHIFT_REPORTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <TimesheetEntriesShiftReportsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_TIMESHEET_ENTRIES}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>TODO: AR-348</LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_SHIFT_REPORTS}/new`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>SHIFT_REPORTS_NEW</LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_SHIFT_REPORTS}/:id`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ShiftReportEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTOR_VISITS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorPanelVisitsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTOR_VISITS_EDIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorPanelVisitsEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTOR_CARDS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorPanelCardsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTOR_CARD}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorPanelCardDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTOR_PANEL_PLANNED_HOURS}
                element={
                  <ContractorPlannedHoursRouteGuard>
                    <LayoutSwitcher>
                      <ContractorPanelPlannedHoursDirectory />
                    </LayoutSwitcher>
                  </ContractorPlannedHoursRouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS_CONTRACTOR_CARDS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorCardsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PROJECTS_CONTRACTOR_CARD}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorCardDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_UPDATES}
                element={
                  <RouteGuard permission={Operation.Export}>
                    <LayoutSwitcher>
                      <Updates />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLLS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollStatusBasedRedirect />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_PREVIEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollPreviewDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_AGGREGATION}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollAggregationDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_INSPECTION}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollInspectionDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_SPLIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollSplitDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_INSPECTION_DETAIL}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollInspectionDetail />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_AGGREGATION_DETAIL}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollAggregationDetail />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_PAYROLL_SPLIT_DETAIL}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <PayrollSplitDetail />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_DEFAULT_STORE_PROJECT_ASSIGNEES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <DefaultStoreProjectAssigneesDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <DefaultStoreProjectAssigneesCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_EDIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <DefaultStoreProjectAssigneesEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_STORE_PROJECT_ASSIGNEES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StoreProjectAssigneesDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_STORE_PROJECT_ASSIGNEES_NEW}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StoreProjectAssigneesCreator />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_STORE_PROJECT_ASSIGNEES_EDIT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <StoreProjectAssigneesEditor />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_AUDIT_LOGS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <AuditLogsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={`${PATH_AUDIT_LOGS}/:id`}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <AuditLogDetails />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_SCHEDULES_REPORT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <SchedulesReport />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_LIMITS_REPORT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <LimitsReport />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTRACTORS_PLANNED_HOURS_APPROVALS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContractorsPlannedHoursApprovalsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_EMAIL_RECIPIENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <EmailRecipientsDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_EMAIL_TYPES}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <EmailTypesList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_EMAIL_TYPE}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <EmailTypeEditorDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_ALL}
                element={
                  <AuthGuard>
                    <LayoutSwitcher>
                      <RouteNotFound />
                    </LayoutSwitcher>
                  </AuthGuard>
                }
              />
              <Route
                path={PATH_PERMISSION_DENIED}
                element={
                  <AuthGuard>
                    <LayoutSwitcher>
                      <RouteDenied />
                    </LayoutSwitcher>
                  </AuthGuard>
                }
              />
              <Route
                path={PATH_CONTEST_AGREEMENT}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContestAgreementDirectory />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTEST_AGREEMENTS}
                element={
                  <RouteGuard>
                    <LayoutSwitcher>
                      <ContestAgreementsList />
                    </LayoutSwitcher>
                  </RouteGuard>
                }
              />
              <Route
                path={PATH_CONTEST_AGREEMENTS_LINK}
                element={<ContestAgreements />}
              />
            </Routes>
            {appConfiguration.enableReactQueryDevtools && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </QueryClientProvider>
        </APIProvider>
      </AccountProvider>
    </Router>
  );
}
